import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminActions, AdminDeleteButton } from "@/components/AdminActions";
import { api, fileUrl } from "@/lib/api";
import { formatDate, formatRupiah, REMINDER_META } from "@/lib/format";
import { Truck, ArrowLeft, Loader2, MapPin, Paperclip, CheckCircle2, History, CreditCard, ArrowLeftRight, ReceiptText, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { VehicleDialog } from "@/pages/Vehicles";
import { EditHistory } from "@/components/AdminEdit";

const ITEM_LABEL = { pajak: "Pajak", kir: "KIR", servis: "Servis", oli: "Ganti Oli" };
const NOTE_FIELDS = [{ k: "date", l: "Tanggal", t: "date" }, { k: "note", l: "Catatan" }];
const CREDIT_FIELDS = [{ k: "date", l: "Tanggal Bayar", t: "date" }, { k: "amount", l: "Nominal", t: "money" }, { k: "note", l: "Catatan" }];
const MAINT_FIELDS = [{ k: "date", l: "Tanggal Dikerjakan", t: "date" }, { k: "actual_cost", l: "Biaya", t: "money" }, { k: "note", l: "Catatan" }];
const PAY_METHOD = { auto_debit: "Auto Debit", transfer: "Transfer", cash: "Cash" };
const CASH_STATUS = {
  pending: { label: "Menunggu", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  approved: { label: "Disetujui", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  disbursed: { label: "Dicairkan", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  rejected: { label: "Ditolak", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

function ReminderPill({ item }) {
  const m = REMINDER_META[item.status] || REMINDER_META.unknown;
  return (
    <div className={`rounded-lg border px-2.5 py-1.5 ${m.cls}`} data-testid={`detail-reminder-${item.key}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide">{item.label}</p>
      <p className="text-xs">{item.due_date ? formatDate(item.due_date) : "—"}</p>
      <p className="text-[10px] font-medium">
        {item.status === "overdue" ? `Terlambat ${Math.abs(item.days)} hr`
          : item.status === "upcoming" ? `${item.days} hari lagi`
          : item.status === "ok" ? "Aman" : "Belum diisi"}
      </p>
    </div>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [rk, setRk] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [item, setItem] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/vehicles/${id}`).then((r) => { setVehicle(r.data); setRk((x) => x + 1); })
      .catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const [cashReqs, setCashReqs] = useState([]);
  useEffect(() => {
    api.get(`/vehicles/${id}/cash-requests`).then((r) => setCashReqs(r.data)).catch(() => {});
  }, [id]);

  const { user } = useAuth();
  const canPayCredit = ["tim_kendaraan", "direktur", "it_admin", "tim_keuangan"].includes(user?.role);
  const canTransfer = ["tim_kendaraan", "it_admin", "direktur"].includes(user?.role);
  const [paying, setPaying] = useState(false);
  const payInstallment = async () => {
    if (!window.confirm("Tandai cicilan bulan ini sudah dibayar?")) return;
    setPaying(true);
    try { await api.post(`/vehicles/${id}/pay-installment`, {}); toast.success("Cicilan ditandai dibayar."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal menandai cicilan."); }
    finally { setPaying(false); }
  };

  const inRange = (d) => {
    const dd = (d || "").slice(0, 10);
    if (!dd) return false;
    if (start && dd < start) return false;
    if (end && dd > end) return false;
    return true;
  };

  const allRecords = (vehicle?.maintenance_records || []).slice().reverse();
  const records = allRecords.filter((r) =>
    (start || end ? inRange(r.date) : true) && (item === "all" ? true : r.item_key === item));
  const totalCost = records.reduce((sum, r) => sum + (r.actual_cost || 0), 0);

  if (loading) {
    return <div className="space-y-2.5 py-4" data-testid="vehicle-detail-loading">{[0, 1, 2, 3].map((i) => (<div key={i} className={`h-4 rounded-md skeleton-shimmer animate-fade-in-up stagger-${i + 1}`} />))}</div>;
  }
  if (notFound || !vehicle) {
    return (
      <Card className="p-10 text-center space-y-3" data-testid="vehicle-detail-notfound">
        <p className="text-muted-foreground">Kendaraan tidak ditemukan.</p>
        <Link to="/vehicles"><Button variant="outline" className="border-brand text-brand"><ArrowLeft className="h-4 w-4 mr-2" /> Kembali</Button></Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/vehicles" className="text-sm text-brand hover:underline flex items-center gap-1 mb-1" data-testid="back-to-vehicles">
            <ArrowLeft className="h-4 w-4" /> Data Kendaraan
          </Link>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Truck className="h-6 w-6 text-brand" /> {vehicle.plate_number}
          </h2>
          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
            {vehicle.vehicle_type} <span className="text-muted-foreground/50">·</span>
            <MapPin className="h-3.5 w-3.5 text-brand" /> {vehicle.branch || "Tanpa Cabang"}
            {vehicle.vehicle_year && <span className="text-muted-foreground/50">·</span>}
            {vehicle.vehicle_year && <span>Tahun {vehicle.vehicle_year}{vehicle.vehicle_age != null ? ` (${vehicle.vehicle_age} th)` : ""}</span>}
          </p>
          {vehicle.age_warning && (
            <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-200" data-testid="vehicle-age-warning">
              <AlertTriangle className="h-3.5 w-3.5" /> Umur {vehicle.vehicle_age} tahun — melewati target 8 tahun, perlu evaluasi
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "it_admin" && (
            <span className="inline-flex items-center gap-1 text-sm text-brand border border-brand/40 rounded-md pl-2" data-testid="admin-edit-vehicle">
              Edit <VehicleDialog vehicle={vehicle} onDone={load} />
            </span>
          )}
          {canTransfer && <TransferDialog vehicle={vehicle} onDone={load} />}
          <AdminDeleteButton path={`/admin/records/vehicles/${vehicle.id}`} onDone={() => navigate("/vehicles")} label="Hapus Kendaraan" testId="admin-del-vehicle"
            confirmText="Kendaraan beserta seluruh riwayat perawatan, mutasi, cicilan, dan tagihan kendaraannya akan dihapus permanen." />
        </div>
      </div>

      <EditHistory entityType="vehicle" entityId={vehicle.id} refreshKey={rk} />

      <OilDiscussionCard vehicle={vehicle} canDecide={user?.role === "direktur"} onDone={load} />

      {(vehicle.transfer_history || []).length > 0 && (
        <Card className="p-5" data-testid="transfer-history-card">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-3" style={{ fontFamily: "Outfit" }}>
            <ArrowLeftRight className="h-4 w-4 text-brand" /> Riwayat Mutasi Cabang
          </h3>
          <div className="space-y-2">
            {vehicle.transfer_history.slice().reverse().map((t, i) => (
              <div key={t.id || i} className="flex items-center justify-between gap-3 text-sm border-b last:border-0 pb-2 last:pb-0" data-testid={`transfer-row-${i}`}>
                <div className="min-w-0">
                  <p className="font-medium flex items-center gap-1.5">{t.from_branch} <ArrowLeftRight className="h-3.5 w-3.5 text-brand" /> {t.to_branch}</p>
                  {t.note && <p className="text-xs text-muted-foreground truncate">{t.note}</p>}
                </div>
                <div className="text-right shrink-0 flex items-center gap-2">
                  <div>
                    <p className="text-xs">{formatDate(t.date)}</p>
                    <p className="text-[11px] text-muted-foreground">{t.by}</p>
                  </div>
                  <AdminActions editPath={`/admin/sub/transfers/${vehicle.id}/${t.id}`} deletePath={`/admin/sub/transfers/${vehicle.id}/${t.id}`} fields={NOTE_FIELDS} values={t} onDone={load} title="Edit Mutasi" testId={`admin-transfer-${i}`} confirmText="Riwayat mutasi dihapus permanen. Cabang kendaraan mengikuti mutasi terakhir yang tersisa." />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {cashReqs.length > 0 && (
        <Card className="p-5" data-testid="vehicle-cash-history-card">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-3" style={{ fontFamily: "Outfit" }}>
            <ReceiptText className="h-4 w-4 text-brand" /> Riwayat Pengajuan Kas Terkait
          </h3>
          <div className="space-y-2">
            {cashReqs.map((c) => (
              <Link to={`/requests/${c.id}`} key={c.id} data-testid={`vehicle-cashreq-${c.id}`}
                className="flex items-center justify-between gap-3 border-b last:border-0 pb-2 last:pb-0 hover:bg-secondary/40 rounded px-1 -mx-1 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.action || "Pengajuan Kas"} · {c.requester_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{formatDate(c.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-mono font-semibold">{formatRupiah(c.amount_requested)}</p>
                  <Badge variant="outline" className={CASH_STATUS[c.status]?.cls}>{CASH_STATUS[c.status]?.label || c.status}</Badge>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5">
        <h3 className="font-semibold text-sm mb-3" style={{ fontFamily: "Outfit" }}>Status Reminder Saat Ini</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {(vehicle.reminders || []).map((it) => <ReminderPill key={it.key} item={it} />)}
        </div>
        <p className="text-xs text-muted-foreground mt-3">Interval servis kendaraan ini: <span className="font-semibold text-brand">{vehicle.service_interval_days || 180} hari</span></p>
      </Card>

      {vehicle.has_credit && (
        <Card className="p-5" data-testid="vehicle-credit-card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-semibold text-sm flex items-center gap-2" style={{ fontFamily: "Outfit" }}><CreditCard className="h-4 w-4 text-brand" /> Data Kredit Kendaraan</h3>
            <Badge className={vehicle.credit_status === "lunas" ? "bg-emerald-600" : "bg-amber-500"} data-testid="credit-status-badge">{vehicle.credit_status === "lunas" ? "LUNAS" : "KREDIT"}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div><p className="text-xs text-muted-foreground">Leasing / Kreditur</p><p className="font-medium">{vehicle.credit_lessor || "-"}</p></div>
            <div><p className="text-xs text-muted-foreground">Total Harga</p><p className="font-mono">{formatRupiah(vehicle.credit_total_price || 0)}</p></div>
            <div><p className="text-xs text-muted-foreground">Cicilan / Bulan</p><p className="font-mono">{formatRupiah(vehicle.credit_monthly || 0)}</p></div>
            <div><p className="text-xs text-muted-foreground">Sisa Bulan</p><p className="font-semibold">{vehicle.credit_months_remaining || 0} bulan</p></div>
            <div><p className="text-xs text-muted-foreground">Jatuh Tempo</p><p className="font-medium">{vehicle.credit_due_date ? formatDate(vehicle.credit_due_date) : "-"}</p></div>
            <div><p className="text-xs text-muted-foreground">Cara Bayar</p><p className="font-medium">{PAY_METHOD[vehicle.credit_payment_method] || "-"}</p></div>
          </div>
          {canPayCredit && vehicle.credit_status !== "lunas" && (
            <Button onClick={payInstallment} disabled={paying} className="mt-4 bg-brand hover:bg-emerald-800" data-testid="pay-installment-btn">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Tandai Cicilan Dibayar</>}
            </Button>
          )}
          {(vehicle.credit_payments || []).length > 0 && (
            <div className="mt-4 border-t pt-3">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Riwayat Pembayaran Cicilan</p>
              <div className="space-y-1">
                {vehicle.credit_payments.slice().reverse().map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between text-xs" data-testid={`credit-payment-${i}`}>
                    <span>{formatDate(p.date)} · {formatRupiah(p.amount || 0)}</span>
                    <span className="text-muted-foreground flex items-center gap-1">sisa {p.months_remaining_after} bln{p.next_due ? ` · jatuh tempo ${formatDate(p.next_due)}` : " · LUNAS"}
                      <AdminActions editPath={`/admin/sub/credit-payments/${vehicle.id}/${p.id}`} deletePath={`/admin/sub/credit-payments/${vehicle.id}/${p.id}`} fields={CREDIT_FIELDS} values={p} onDone={load} title="Edit Pembayaran Cicilan" testId={`admin-credit-${i}`} confirmText="Pembayaran cicilan dihapus permanen. Sisa bulan cicilan bertambah 1 dan status kembali KREDIT." />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Tanggal Mulai</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-9" data-testid="detail-start-date" />
            </div>
            <div>
              <Label className="text-xs">Tanggal Akhir</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-9" data-testid="detail-end-date" />
            </div>
            <Button type="button" variant="ghost" size="sm" data-testid="detail-reset-filter"
              onClick={() => { setStart(""); setEnd(""); setItem("all"); }}>Semua</Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[["all", "Semua"], ["pajak", "Pajak"], ["kir", "KIR"], ["servis", "Servis"], ["oli", "Ganti Oli"]].map(([v, l]) => (
              <Button key={v} type="button" size="sm" variant={item === v ? "default" : "outline"}
                className={item === v ? "bg-brand hover:bg-emerald-800 h-8" : "h-8"}
                onClick={() => setItem(v)} data-testid={`detail-item-filter-${v}`}>{l}</Button>
            ))}
          </div>
        </div>
        <div className="mt-4 rounded-lg border bg-emerald-50/60 px-4 py-3 flex items-center justify-between" data-testid="detail-total-cost">
          <span className="text-sm font-medium text-emerald-800">Total Biaya Perawatan ({records.length} pengerjaan)</span>
          <span className="text-lg font-bold font-mono text-brand">{formatRupiah(totalCost)}</span>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <History className="h-4 w-4 text-brand" />
          <h3 className="font-semibold text-sm" style={{ fontFamily: "Outfit" }}>Riwayat Pengerjaan</h3>
        </div>
        {records.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="detail-history-empty">Belum ada riwayat pengerjaan pada filter ini.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="detail-history-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Tanggal</th>
                  <th className="text-left px-4 py-2.5">Item</th>
                  <th className="text-right px-4 py-2.5">Biaya</th>
                  <th className="text-left px-4 py-2.5">Jatuh Tempo Berikutnya</th>
                  <th className="text-left px-4 py-2.5">Catatan</th>
                  <th className="text-left px-4 py-2.5">Oleh</th>
                  <th className="text-left px-4 py-2.5">Bukti</th>
                  {user?.role === "it_admin" && <th className="px-4 py-2.5"></th>}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={r.id || i} className="border-t align-top" data-testid={`detail-history-row-${i}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-4 py-2.5"><Badge variant="outline" className="border-brand text-brand">{r.label || ITEM_LABEL[r.item_key] || r.item_key}</Badge></td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.actual_cost > 0 ? formatRupiah(r.actual_cost) : "-"}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{r.next_due ? formatDate(r.next_due) : "-"}</td>
                    <td className="px-4 py-2.5 max-w-[220px]">{r.note || "-"}</td>
                    <td className="px-4 py-2.5">{r.done_by || "-"}</td>
                    <td className="px-4 py-2.5">
                      {r.file_id ? (
                        <a href={fileUrl(r.file_id)} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1 text-brand hover:underline" data-testid={`detail-proof-${i}`}>
                          <Paperclip className="h-3.5 w-3.5" /> Lihat
                        </a>
                      ) : (
                        <span className="flex items-center gap-1 text-emerald-600" data-testid={`detail-done-${i}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Selesai
                        </span>
                      )}
                    </td>
                    {user?.role === "it_admin" && (
                      <td className="px-4 py-2.5 text-right"><AdminActions editPath={`/admin/sub/maintenance/${vehicle.id}/${r.id}`} deletePath={`/admin/sub/maintenance/${vehicle.id}/${r.id}`} fields={MAINT_FIELDS} values={r} onDone={load} title="Edit Riwayat Pengerjaan" testId={`admin-maint-${i}`} confirmText="Riwayat pengerjaan (termasuk nota cabang) dihapus permanen. Tanggal pengerjaan terakhir mengikuti riwayat yang tersisa." /></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function OilDiscussionCard({ vehicle, canDecide, onDone }) {
  const [busy, setBusy] = useState(false);
  const disc = vehicle.oil_discussion;
  if (!disc) return null;
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/oil/decision`, { action, note: "" });
      toast.success(action === "approve" ? "Disetujui — ganti oli bisa ditandai dikerjakan." : "Ganti oli ditunda +15 hari.");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal memproses keputusan."); }
    finally { setBusy(false); }
  };
  const meta = {
    pending: ["Perlu Diskusi", "border-amber-300 bg-amber-50 text-amber-900"],
    approved: ["Disetujui", "border-emerald-300 bg-emerald-50 text-emerald-900"],
    postponed: ["Ditunda", "border-slate-300 bg-slate-50 text-slate-800"],
  }[disc.status] || ["", "border-border"];
  const km = (n) => (n == null ? "-" : Math.round(n).toLocaleString("id-ID"));
  return (
    <Card className={`p-5 border ${meta[1]}`} data-testid="oil-discussion-card">
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-2" style={{ fontFamily: "Outfit" }}>
        <AlertTriangle className="h-4 w-4" /> Ganti Oli — {meta[0]}
      </h3>
      <p className="text-sm">KM saat diajukan: <span className="font-mono">{km(disc.current_odometer)}</span> · Tempuh sejak oli terakhir: <span className="font-mono">{km(disc.km_since)} km</span></p>
      <p className="text-sm mt-1">Keterangan: {disc.note || "-"}</p>
      {disc.decision_note && <p className="text-xs mt-1">Catatan Direktur: {disc.decision_note}</p>}
      {disc.postpone_until && <p className="text-xs mt-1">Dijadwalkan ulang sampai {formatDate(disc.postpone_until)}</p>}
      {disc.status !== "pending" && <div className="mt-2"><AdminDeleteButton path={`/admin/reset/oil-decision/${vehicle.id}`} onDone={onDone} label="Hapus Keputusan Direktur" testId="admin-reset-oil" confirmText="Keputusan Direktur dihapus permanen. Diskusi ganti oli kembali ke status Perlu Diskusi." /></div>}
      {canDecide && disc.status === "pending" && (
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" disabled={busy} onClick={() => decide("approve")} className="bg-brand hover:bg-emerald-800" data-testid="oil-approve-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve (Ganti Sekarang)"}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => decide("postpone")} data-testid="oil-postpone-btn">Tunda +15 Hari</Button>
        </div>
      )}
    </Card>
  );
}

function TransferDialog({ vehicle, onDone }) {
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [moveDate, setMoveDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [open]);

  const reset = () => { setBranchId(""); setMoveDate(""); setNote(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!branchId) { toast.error("Pilih cabang tujuan"); return; }
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/transfer`, { branch_id: branchId, date: moveDate || undefined, note });
      toast.success("Kendaraan dipindahkan ke cabang baru.");
      setOpen(false); reset(); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal memindahkan kendaraan."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-brand text-brand hover:bg-secondary" data-testid="transfer-vehicle-btn">
          <ArrowLeftRight className="h-4 w-4 mr-2" /> Pindah Cabang
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Pindah Cabang — {vehicle.plate_number}</DialogTitle>
          <DialogDescription>Cabang saat ini: {vehicle.branch || "Tanpa Cabang"}. Pilih cabang tujuan untuk memindahkan kendaraan.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Cabang Tujuan</Label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="transfer-branch-select">
              <option value="">Pilih cabang tujuan</option>
              {branches.filter((b) => b.id !== vehicle.branch_id).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Tanggal Pindah</Label>
            <Input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)} className="mt-1.5" data-testid="transfer-date-input" />
            <p className="text-[11px] text-muted-foreground mt-1">Kosong = hari ini</p>
          </div>
          <div>
            <Label>Catatan (opsional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder="Alasan / keterangan mutasi" data-testid="transfer-note-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="submit-transfer-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pindahkan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
